import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { RegisterPayload, User } from '../models/user.model';

export interface UpdateProfilePayload {
  fullName?: string;
  currentPassword?: string;
  newPassword?: string;
  country?: string | null;
  city?: string | null;
  zipCode?: string | null;
}

const TOKEN_KEY = 'myfamilio_token';
const REFRESH_TOKEN_KEY = 'myfamilio_refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = '/api/auth';

  currentUser = signal<User | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  login(email: string, password: string): Observable<{ token: string; refresh_token: string }> {
    return this.http.post<{ token: string; refresh_token: string }>(`${this.apiUrl}/login`, { email, password }).pipe(
      tap(res => {
        localStorage.setItem(TOKEN_KEY, res.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
        this.loadCurrentUser();
      }),
    );
  }

  refreshToken(): Observable<{ token: string; refresh_token: string }> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return this.http
      .post<{ token: string; refresh_token: string }>(`${this.apiUrl}/token/refresh`, { refresh_token: refreshToken })
      .pipe(
        tap(res => {
          localStorage.setItem(TOKEN_KEY, res.token);
          localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token);
        }),
      );
  }

  register(payload: RegisterPayload): Observable<{ message: string; user: User }> {
    return this.http.post<{ message: string; user: User }>(`${this.apiUrl}/register`, payload);
  }

  loadCurrentUser(): void {
    this.http.get<User>(`${this.apiUrl}/me`).subscribe({
      next: user => this.currentUser.set(user),
      error: () => this.currentUser.set(null),
    });
  }

  logout(): void {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      this.http.post(`${this.apiUrl}/logout`, { refresh_token: refreshToken }).subscribe({ error: () => undefined });
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  updateProfile(payload: UpdateProfilePayload): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/me`, payload).pipe(tap(user => this.currentUser.set(user)));
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }
}
